import React, { forwardRef } from 'react';
import SectionHeader from './SectionHeader';
import { Button } from '../../../../components/ui/button';

// Props interface for the Section component
interface SectionProps {
  title: string;                   // Section title displayed in header
  children: React.ReactNode;       // Default content
  className?: string;              // Optional additional classes
  id: string;                      // Section ID for navigation
  onRegenerate?: (e: React.MouseEvent) => void;  // Handler for regenerate button click
  onEdit?: (e: React.MouseEvent) => void;        // Handler for edit button click
  onSaveRegenerated?: () => void;  // Handler for saving regenerated content
  onDiscardRegenerated?: () => void;  // Handler for discarding regenerated content
  onSaveEdited?: () => void;       // Handler for saving edited content
  onDiscardEdited?: () => void;    // Handler for discarding edited content
  regeneratedContent?: any;        // New content when regenerated
  isRegenerated?: boolean;         // Whether section is showing regenerated content
  isEditing?: boolean;             // Whether section is in editing mode
  renderRegeneratedContent?: (content: any) => React.ReactNode;  // Custom renderer for regenerated content
  renderEditableContent?: (content: any, onChange: (newContent: any) => void) => React.ReactNode;  // Custom renderer for editable content
  editableContent?: any;           // Current editable content
  onEditableContentChange?: (newContent: any) => void;  // Handler for editable content changes
  showEditIcon?: boolean;          // Whether to show the edit icon
}

// Section component that handles displaying content and regeneration UI
const Section = forwardRef<HTMLDivElement, SectionProps>(
  ({ 
    title, 
    children, 
    className = '', 
    id, 
    onRegenerate,
    onEdit,
    onSaveRegenerated,
    onDiscardRegenerated,
    onSaveEdited,
    onDiscardEdited,
    regeneratedContent,
    isRegenerated,
    isEditing,
    renderRegeneratedContent,
    renderEditableContent,
    editableContent,
    onEditableContentChange,
    showEditIcon = true
  }, ref) => {
    return (
      <div ref={ref} id={id} className={`mb-8 ${className}`}>
        {/* Section header with regenerate and edit buttons */}
        <SectionHeader 
          title={title} 
          onRegenerate={onRegenerate}
          onEdit={onEdit}
          showEditIcon={showEditIcon}
        />
        
        {/* Content container with theme-aware backgrounds */}
        <div className={`rounded-lg text-text-primary transition-colors duration-300 p-3 ${
          isRegenerated || isEditing 
            ? 'bg-primaryAccent-4 dark:bg-primaryAccent-12/70' // Light purple in light mode, dark purple with transparency in dark mode
            : 'bg-background'
        }`}>
          {/* Render appropriate content based on state */}
          {isRegenerated && renderRegeneratedContent ? 
            renderRegeneratedContent(regeneratedContent) : 
            isEditing && renderEditableContent ? 
              renderEditableContent(editableContent, onEditableContentChange!) : 
              children}
        </div>
        
        {/* Save/Discard buttons for regenerated content */}
        {isRegenerated && (
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="ghost"
              onClick={onDiscardRegenerated}
              className="text-xs text-text-primary hover:text-text-secondary"
              size="sm"
            >
              Discard
            </Button>
            <Button
              variant="ghost"
              onClick={onSaveRegenerated}
              className="text-xs text-primary hover:text-primary/80"
              size="sm"
            >
              Save
            </Button>
          </div>
        )}

        {/* Save/Discard buttons for edited content */}
        {isEditing && (
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="ghost"
              onClick={onDiscardEdited}
              className="text-xs text-text-primary hover:text-text-secondary"
              size="sm"
            >
              Discard
            </Button>
            <Button
              variant="customPrimary"
              onClick={onSaveEdited}
              className="text-xs text-primary hover:text-primary/80"
              size="sm"
            >
              Save
            </Button>
          </div>
        )}
      </div>
    );
  }
);

Section.displayName = 'Section';

export default Section;